/**
 * localNotifications — schedule on-device reminders before bookmarked sessions.
 *
 * Uses @capacitor/local-notifications (native only). On web the plugin is a
 * no-op, so callers get a graceful `{ scheduled: 0, supported: false }`.
 *
 * We derive a stable integer notification id from each session UUID so that
 * re-scheduling replaces the previous reminder instead of duplicating it.
 */
import { Capacitor } from '@capacitor/core';
import type { Session } from '@conference/shared';

const DEFAULT_LEAD_MINUTES = 10;

/** Deterministic positive 31-bit int from a string (for notification ids). */
function stableId(uuid: string): number {
  let h = 0;
  for (let i = 0; i < uuid.length; i++) {
    h = (Math.imul(31, h) + uuid.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 2_000_000_000 || 1;
}

function sessionStart(session: Session): Date | null {
  const raw = `${session.date}T${session.start_time}`;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isLocalNotificationsSupported(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Ensure permission is granted. Returns true if we may post notifications.
 */
export async function ensureNotificationPermission(): Promise<boolean> {
  if (!isLocalNotificationsSupported()) return false;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const check = await LocalNotifications.checkPermissions();
    if (check.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === 'granted';
  } catch (err) {
    console.warn('LocalNotifications permission check failed', err);
    return false;
  }
}

/**
 * Schedule reminders for the given (bookmarked) sessions. Past sessions and
 * sessions whose reminder time has already elapsed are skipped. Existing
 * reminders for the same sessions are replaced (same stable id).
 */
export async function scheduleSessionReminders(
  sessions: Session[],
  leadMinutes = DEFAULT_LEAD_MINUTES,
): Promise<{ scheduled: number; supported: boolean }> {
  if (!isLocalNotificationsSupported()) return { scheduled: 0, supported: false };

  const granted = await ensureNotificationPermission();
  if (!granted) return { scheduled: 0, supported: true };

  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const now = Date.now();

    const toSchedule = sessions
      .map((s) => ({ s, start: sessionStart(s) }))
      .filter((x): x is { s: Session; start: Date } => x.start !== null)
      .map(({ s, start }) => ({
        s,
        at: new Date(start.getTime() - leadMinutes * 60_000),
      }))
      .filter(({ at }) => at.getTime() > now);

    if (toSchedule.length === 0) return { scheduled: 0, supported: true };

    await LocalNotifications.schedule({
      notifications: toSchedule.map(({ s, at }) => ({
        id: stableId(s.id),
        title: `Starting soon: ${s.title}`,
        body: [s.room ? `📍 ${s.room}` : '', `Begins in ${leadMinutes} min`].filter(Boolean).join(' · '),
        // Session reminders are optional; avoid Android's special exact-alarm permission.
        isExactNotification: false,
        schedule: { at },
        extra: { sessionId: s.id },
      })),
    });

    return { scheduled: toSchedule.length, supported: true };
  } catch (err) {
    console.warn('scheduleSessionReminders failed', err);
    return { scheduled: 0, supported: true };
  }
}

/** Cancel a single session's reminder (e.g. when the user un-bookmarks it). */
export async function cancelSessionReminder(sessionId: string): Promise<void> {
  if (!isLocalNotificationsSupported()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id: stableId(sessionId) }] });
  } catch { /* ignore */ }
}
